import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { useMemo, useRef, useState } from 'react';
import {
  GetPlantOverviewResponse,
  useGetPlantOverviewQuery,
} from '@/features/plants/hooks/queries/[plantId]/use-get-plant-overview.ts';
import { Button } from '@/components/ui/button.tsx';
import { cn } from '@/utils/utils.ts';
import { PlantPowerFlow3Sources } from '@/assets/plant/power-flow-3-sources.tsx';
import { useGetChartBESSQuery } from '@/features/plants/components/plant-overview/storage-only/hooks/queries/[bess]/get-bess-charts.ts';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { enUS, ro } from 'date-fns/locale';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import type { Props as LabelProps } from 'recharts/types/component/Label';
import { format, Locale } from 'date-fns';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Zap, BarChart3, FileText } from 'lucide-react';

const getPlantDetails = (plant: TPlant | undefined) => {
  if (!plant) return [];

  return [
    {
      label: 'Name',
      value: plant.basicDetails.name,
    },
    {
      label: 'Type',
      value: `${plant?.basicDetails.type}`,
    },
    {
      label: 'Address',
      value: `${plant?.siteInformation.country.value}, ${plant?.siteInformation.city.value}`,
    },
    {
      label: 'Connection date',
      value: format(new Date(plant?.createdAt), 'dd MMM yyyy'),
    },
    {
      label: 'Site area',
      value: `${plant?.siteInformation.siteArea} m²`,
    },
    {
      label: 'Capacity',
      value: `${plant?.technicalSpecifications.capacity} kW`,
    },
    {
      label: 'Storage capacity',
      value: `${plant?.technicalSpecifications.storageCapacity} kWh`,
    },
    {
      label: 'Tariff rate',
      value: `${plant?.financialInformation.tariffRate}€/kWh`,
    },
    {
      label: 'Owner email',
      value: plant?.contactDetails.ownerEmail,
    },
    {
      label: 'Installer email',
      value: plant?.contactDetails.installerEmail,
    },
  ];
};

const calculateMeasurements = (overview: GetPlantOverviewResponse | undefined | null) => {
  return {
    consumption: Number(overview?.currentConsumptionPower?.toFixed(2)),
    grid: Number(overview?.currentGridPower?.toFixed(2)),
    batteryPower: Number(overview?.currentBatteryPower?.toFixed(2)),
    batterySoC: Number(overview?.currentBatterySoC?.toFixed(1)),
  };
};

export const PlantStorageOnlyDetailsCard = ({
  plantId,
  className,
}: {
  plantId?: string;
  isLoading?: boolean;
  className?: string;
}) => {
  const [tab, setTab] = useState<'power-flow' | 'charge-discharge' | 'plant-details'>(
    'power-flow'
  );
  const [isOpen, setIsOpen] = useState(false);

  useGetChartBESSQuery({ id: plantId });

  const { data: overview, isLoading: isPlantOverviewLoading } = useGetPlantOverviewQuery({
    plantId: plantId,
  });

  const { data: plant } = useGetPlantQuery({ plantId });

  const measurements = calculateMeasurements(overview);

  const getTabTitle = () => {
    switch (tab) {
      case 'power-flow':
        return 'Power flow';
      case 'charge-discharge':
        return 'Charge/Discharge Cycles';
      case 'plant-details':
        return 'Plant details';
      default:
        return 'Power flow';
    }
  };

  const menuItems = [
    { value: 'power-flow' as const, label: 'Power Flow', icon: Zap },
    { value: 'charge-discharge' as const, label: 'Charge/Discharge Cycles', icon: BarChart3 },
    { value: 'plant-details' as const, label: 'Plant Details', icon: FileText },
  ];

  const currentItem = menuItems.find((item) => item.value === tab);

  return (
    <DashboardCard className={cn('text-nowrap', className)} isLoading={isPlantOverviewLoading}>
      <div className="flex w-full justify-between">
        <h3 className="flex flex-col gap-1 font-semibold text-nowrap">{getTabTitle()}</h3>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[48px] border-2 border-gray-300 p-2">
              {currentItem && <currentItem.icon className="size-4" />}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              'relative mr-4 w-55 overflow-hidden rounded-lg border bg-white/95 p-1 shadow-xl backdrop-blur-xl dark:bg-gray-900/95'
            )}
          >
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.value}
                  onClick={() => {
                    setTab(item.value);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
                    'text-left',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
                    'disabled:pointer-events-none disabled:opacity-50',
                    tab === item.value && 'bg-accent/50'
                  )}
                >
                  <Icon className="size-4" />
                  {item.label}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
      </div>
      <div className="relative flex min-w-[240px] flex-1 flex-col gap-4">
        {tab === 'power-flow' && (
          <div className="flex w-full flex-1 justify-between">
            <div className="flex h-full w-full items-center justify-center">
              <PlantPowerFlow3Sources
                width="100%"
                height="100%"
                className="max-w-[26rem]"
                measurements={measurements}
              />
            </div>
          </div>
        )}
        {tab === 'charge-discharge' && (
          <PlantStorageOnlyChargeDischargePowerChart plantId={plantId} />
        )}
        {tab === 'plant-details' &&
          getPlantDetails(plant).map((detail) => (
            <div key={detail.label} className="grid grid-cols-[minmax(120px,40%)_1fr] gap-4">
              <span className="text-muted-foreground">{detail.label}: </span>
              <span className="overflow-hidden font-medium text-ellipsis">{detail.value}</span>
            </div>
          ))}
      </div>
    </DashboardCard>
  );
};

const METRIC_COLORS = {
  BATTERY_CHARGE: '#038BA8',
  BATTERY_DISCHARGE: '#AC44FB',
} as const;

const METRIC_KEYS = {
  BATTERY_CHARGE: 'battery_charge',
  BATTERY_DISCHARGE: 'battery_discharge',
} as const;

const CHART_METRICS = [
  {
    label: 'Battery Charge',
    color: METRIC_COLORS.BATTERY_CHARGE,
    selected: true,
    dataKey: METRIC_KEYS.BATTERY_CHARGE,
    axis: false,
    id: METRIC_KEYS.BATTERY_CHARGE,
  },
  {
    label: 'Battery Discharge',
    color: METRIC_COLORS.BATTERY_DISCHARGE,
    selected: true,
    dataKey: METRIC_KEYS.BATTERY_DISCHARGE,
    axis: false,
    id: METRIC_KEYS.BATTERY_DISCHARGE,
  },
];

function PlantStorageOnlyChargeDischargePowerChart({
  plantId,
}: {
  plantId: string | undefined;
}) {
  const { data } = useGetChartBESSQuery({ id: plantId });
  const chartData = data?.batteryChargeDischargePower || [];

  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width:640px)');
  const chartRef = useRef<HTMLDivElement>(null);
  const [fields, setFields] = useState(CHART_METRICS);

  const locale = (i18n.language as 'ro' | 'en') === 'ro' ? ro : enUS;

  const filteredFields = useMemo(() => {
    return fields.map((field) => ({
      ...field,
      label: t(`metrics.${field.id}`),
    }));
  }, [t, fields]);

  const chart = useMemo(() => {
    if (!chartData)
      return {
        isPowerOver1000: false,
        data: [],
      };

    const processValue = (value: number | null, shouldNegate = false): number | null => {
      if (value === null) return null;
      const absValue = Math.abs(value);
      return shouldNegate ? -absValue : absValue;
    };

    const isPowerOver1000 =
      chartData?.some((c) => c.battery_discharge > 1000 || c.battery_charge > 1000) || false;

    return {
      data: chartData?.map((v) => {
        const battery_charge = isPowerOver1000
          ? (processValue(v?.battery_charge) || 0) / 1000
          : processValue(v?.battery_charge);

        const battery_discharge =
          isPowerOver1000 && v?.battery_discharge
            ? (processValue(v?.battery_discharge) || 0) / 1000
            : processValue(v?.battery_discharge);

        return {
          [METRIC_KEYS.BATTERY_CHARGE]: battery_charge,
          [METRIC_KEYS.BATTERY_DISCHARGE]: battery_discharge,
          time: v?.time,
        };
      }),
      isPowerOver1000,
    };
  }, [chartData]);

  return (
    <>
      <div className={cn(`relative w-full`, isMobile ? 'h-[380px]' : 'h-[410px]')}>
        <div className="absolute top-3 right-12 z-10"></div>
        <div className="h-full w-full">
          <div className="h-full" ref={chartRef}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chart.data}
                margin={{
                  top: 25,
                  right: isMobile ? -60 : -35,
                  bottom: 0,
                  left: isMobile ? -15 : 0,
                }}
              >
                <defs>
                  {fields.map((v) => (
                    <linearGradient key={v.id} id={v.id} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={v.color} stopOpacity={1} />
                      <stop offset="95%" stopColor={v.color} stopOpacity={0.2} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid vertical={false} stroke="#e5e7eb" />
                <XAxis
                  key="xAxis-main"
                  dataKey="time"
                  xAxisId="main"
                  minTickGap={40}
                  tickFormatter={(t) => {
                    return formatDate(t, locale);
                  }}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                />
                <YAxis
                  yAxisId="left"
                  label={{
                    value: chart.isPowerOver1000 ? 'MWh' : 'kWh',
                    angle: -90,
                    position: 'left',
                    offset: isMobile ? -20 : -10,
                  }}
                  style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{
                    value: '%',
                    angle: -90,
                    position: 'right',
                    offset: isMobile ? -30 : -25,
                    textSize: isMobile ? 12 : 16,
                  }}
                  style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                />
                <Tooltip
                  cursor={false}
                  content={(props) => (
                    <ChartTooltipComponent
                      {...props}
                      locale={locale}
                      isPowerOver1000={chart.isPowerOver1000}
                    />
                  )}
                />

                {fields.map((v, index) => {
                  if (!v.selected) return null;

                  return (
                    <Bar
                      key={`bar-${v.dataKey}-${index}`}
                      xAxisId="main"
                      yAxisId={'left'}
                      dataKey={v.dataKey}
                      name={t ? t(`metrics.${v.dataKey}`) : v.label}
                      fill={`${v.color}80`}
                      stroke={v.color}
                      strokeWidth={1}
                      radius={[10, 10, 0, 0]}
                      label={(props: LabelProps) => {
                        const { x, y, value } = props;
                        if (x === undefined || y === undefined) return null;
                        let label = Number(value).toFixed(2);

                        return (
                          <g>
                            <text
                              x={x}
                              y={y}
                              dy={-6}
                              fontSize={isMobile ? 14 : 16}
                              fill="white"
                              stroke="white"
                              strokeWidth={3}
                              strokeLinejoin="round"
                              paintOrder="stroke"
                              textAnchor="end"
                              style={{ pointerEvents: 'none' }}
                            >
                              {label}
                            </text>
                            <text
                              x={x}
                              y={y}
                              dy={-6}
                              fontSize={isMobile ? 14 : 16}
                              fill="#7D7D85"
                              textAnchor="end"
                              style={{ pointerEvents: 'none' }}
                            >
                              {label}
                            </text>
                          </g>
                        );
                      }}
                    />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mx-auto flex flex-wrap gap-1 md:gap-1">
        {filteredFields.map((f) => {
          return (
            <Button
              key={f.dataKey}
              variant="ghost"
              style={{ opacity: !f.selected ? '0.4' : '1' }}
              className="h-min w-min justify-start p-1 px-2"
              onClick={() => {
                setFields((current) =>
                  current.map((c) => {
                    if (c.id === f.id)
                      return {
                        ...c,
                        selected: !c.selected,
                      };

                    return c;
                  })
                );
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="size-4 rounded-full"
                  style={{ backgroundColor: f.color }}
                ></span>
                <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
                  {f.label}
                </span>
              </div>
            </Button>
          );
        })}
      </div>
    </>
  );
}

function ChartTooltipComponent({
  payload,
  label,
  locale,
  isPowerOver1000,
}: TooltipContentProps<ValueType, NameType> & {
  locale: Locale;
  isPowerOver1000: boolean;
}) {
  const formattedDate = label ? formatDate(new Date(label), locale) : '';

  const formatValue = (p: Payload<ValueType, NameType>): string => {
    if (p?.dataKey && p.dataKey === 'battery_state_of_charge') return `${p.value} %`;

    if (!p?.value) return '0 kWh';

    const value = Number(p.value);
    const fixedValue = +value.toFixed(2);
    const isBatteryPower = p.name === 'Battery Power';

    if (!isPowerOver1000) {
      return isBatteryPower ? `${fixedValue} kWh` : `${Math.abs(fixedValue)} kWh`;
    }

    const megaWattValue = value.toFixed(2);
    return isBatteryPower ? `${megaWattValue} MWh` : `${Math.abs(Number(megaWattValue))} MWh`;
  };

  return (
    <div className="flex flex-col border border-gray-300 bg-white p-4 text-base">
      <p>{formattedDate}</p>
      <div className="mt-2">
        {payload?.map((p: Payload<ValueType, NameType>) => (
          <p style={{ color: p.stroke }} key={p.name}>
            {p.name} : {formatValue(p)}
          </p>
        ))}
      </div>
    </div>
  );
}

function formatDate(date: Date, locale?: Locale): string {
  const capitalizeWords = (str: string) =>
    str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  return capitalizeWords(format(date, 'EEE, MMM d', { locale }));
}
